import { cn } from '../../lib/cn.js'

export interface PaymentCardProps {
  /** `visa`, `mastercard`, `amex` — whatever the gateway calls it. */
  brand: string | null | undefined
  /** The last four digits, as stored. Never more than four. */
  last4: string | null | undefined
  empty?: string
  className?: string
}

/** `visa` → `Visa`, `amex` → `Amex`, `american_express` → `American Express`. */
const titled = (brand: string) =>
  brand
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .replace(/\bAmex\b/i, 'Amex')

/**
 * The card a payment came from.
 *
 * 9.3's table has a **card** column between the reference and the date, and 9.3a
 * says the money goes "back to the Visa 4242 it came from" — so the same two
 * fields appear in a sentence and in a cell, and they should read the same way
 * in both.
 *
 * Four digits and a brand, and deliberately nothing else: there is nothing else
 * to show and a longer string invites somebody to think the platform holds more
 * of the card than it does. It holds these two.
 *
 * The dots are `•` rather than `*`, and there are four of them — the shape people
 * recognise from a statement, not a masked password.
 */
export function PaymentCard({ brand, last4, empty = '–', className }: PaymentCardProps) {
  if (!last4) return <span className={cn('text-ink-muted', className)}>{empty}</span>
  return (
    <span className={cn('whitespace-nowrap', className)}>
      {brand && <span>{titled(brand)} </span>}
      <span className="text-ink-muted" aria-hidden>
        ••••
      </span>{' '}
      <span className="tabular-nums">{last4}</span>
      {/* Read aloud as words, because "bullet bullet bullet bullet" is not useful. */}
      <span className="sr-only">ending {last4}</span>
    </span>
  )
}
